import { computed, Injectable, inject, signal, Signal } from '@angular/core';
import { Diagnostic } from '@awesome-cordova-plugins/diagnostic/ngx';
import type { ToastButton } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { DownloadQueueTask, DownloadQueueTaskView } from '@services/download-manager.interfaces';
import { MediaGalleryService } from '@services/media-gallery.service';
import { SaveMediaToGalleryRequest, SaveMediaToGalleryResult } from '@services/media-gallery.interfaces';
import { PlatformService } from '@services/platform.service';
import { ToastService } from '@services/toast.service';

@Injectable({
  providedIn: 'root'
})
export class DownloadManagerService {
  private readonly diagnostic = inject(Diagnostic);
  private readonly mediaGalleryService = inject(MediaGalleryService);
  private readonly platformService = inject(PlatformService);
  private readonly toastService = inject(ToastService);
  private readonly translate = inject(TranslateService);

  private readonly tasksSignal = signal<DownloadQueueTask[]>([]);
  private readonly expandedSignal = signal<boolean>(false);

  public readonly tasks: Signal<DownloadQueueTaskView[]> = computed<DownloadQueueTaskView[]>(() => {
    return this.tasksSignal().map((task: DownloadQueueTask): DownloadQueueTaskView => ({
      id: task.id,
      fileName: task.fileName,
      progress: task.progress,
      status: task.status,
      cancelable: task.cancelable,
    }));
  });

  public readonly hasActiveDownloads: Signal<boolean> = computed<boolean>(() => this.tasksSignal().length > 0);
  public readonly activeTasksCount: Signal<number> = computed<number>(() => this.tasksSignal().length);
  public readonly canExpand: Signal<boolean> = computed<boolean>(() => this.tasksSignal().length > 1);
  public readonly isExpanded: Signal<boolean> = this.expandedSignal.asReadonly();
  public readonly overallProgressPercent: Signal<number> = computed<number>(() => {
    const tasks: DownloadQueueTask[] = this.tasksSignal();
    if (!tasks.length) {
      return 0;
    }

    const summary: number = tasks.reduce((total: number, task: DownloadQueueTask): number => total + task.progress, 0);
    return Math.max(0, Math.min(100, Math.round(summary / tasks.length)));
  });

  public readonly singleTask: Signal<DownloadQueueTaskView | null> = computed<DownloadQueueTaskView | null>(() => {
    const tasks: DownloadQueueTaskView[] = this.tasks();
    return tasks.length === 1 ? tasks[0] : null;
  });

  public enqueueSaveToGallery(request: SaveMediaToGalleryRequest): void {
    const normalizedRequest: SaveMediaToGalleryRequest = {
      ...request,
      url: request.url.trim(),
      fileName: this.resolveDisplayFileName(request.fileName ?? null, request.url),
    };

    const taskId: string = this.createTaskId();
    const abortController = new AbortController();
    const task: DownloadQueueTask = {
      id: taskId,
      request: normalizedRequest,
      fileName: normalizedRequest.fileName ?? this.translate.instant('downloadsToast.unknownFile'),
      progress: 0,
      status: 'downloading',
      cancelable: true,
      cancel: (): void => abortController.abort(),
    };

    this.addTask(task);
    void this.executeTask(taskId, task.request, abortController);
  }

  public toggleExpanded(): void {
    if (!this.canExpand()) {
      return;
    }
    this.expandedSignal.update((current: boolean): boolean => !current);
  }

  public collapse(): void {
    this.expandedSignal.set(false);
  }

  public cancelTask(taskId: string): void {
    const task = this.findTask(taskId);
    if (!task || !task.cancelable) {
      return;
    }

    try {
      task.cancel();
    } catch (error) {
      console.warn('[DownloadManager] cancel failed', { taskId, error });
    }

    this.removeTask(taskId);
    this.finalizeQueueIfIdle();
  }

  private async executeTask(
    taskId: string,
    request: SaveMediaToGalleryRequest,
    abortController: AbortController,
  ): Promise<void> {
    try {
      const result: SaveMediaToGalleryResult = await this.mediaGalleryService.saveRemoteFileToGallery(request, {
        signal: abortController.signal,
        onProgress: (progress): void => {
          this.updateTaskProgress(taskId, progress.percent);
        },
      });
      await this.handleTaskResult(taskId, result);
    } catch (error) {
      console.error('[DownloadManager] task execution failed', { taskId, error });
      await this.handleTaskResult(taskId, 'failed');
    }
  }

  private async handleTaskResult(taskId: string, result: SaveMediaToGalleryResult): Promise<void> {
    switch (result) {
      case 'saved':
        await this.toastService.success(this.translate.instant('userProfile.savedToGallery'));
        break;
      case 'permission-denied':
        await this.toastService.warning(this.translate.instant('userProfile.saveToGalleryPermissionRequired'), {
          buttons: [this.createSettingsToastButton()],
          duration: 10_000,
        });
        break;
      case 'failed':
        await this.toastService.error(this.translate.instant('userProfile.saveToGalleryFailed'));
        break;
      case 'cancelled':
        break;
    }

    this.removeTask(taskId);
    this.finalizeQueueIfIdle();
  }

  private createSettingsToastButton(): ToastButton {
    return {
      text: this.translate.instant('userProfile.buttons.settings'),
      role: 'settings',
      handler: (): void => {
        void this.openApplicationSettings();
      },
    };
  }

  private async openApplicationSettings(): Promise<void> {
    if (!this.platformService.isDevice) {
      return;
    }

    try {
      await this.diagnostic.switchToSettings();
    } catch (error) {
      console.warn('[DownloadManager] failed to open application settings', { error });
    }
  }

  private updateTaskProgress(taskId: string, percent: number): void {
    const normalizedPercent: number = Math.max(0, Math.min(100, Math.round(percent)));

    this.updateTask(taskId, (task: DownloadQueueTask): DownloadQueueTask => {
      if (normalizedPercent <= task.progress) {
        return task;
      }

      return {
        ...task,
        progress: normalizedPercent,
        status: normalizedPercent >= 100 ? 'saving' : 'downloading',
        cancelable: normalizedPercent < 100,
      };
    });
  }

  private addTask(task: DownloadQueueTask): void {
    const next: DownloadQueueTask[] = [...this.tasksSignal(), task];
    this.setTasks(next);
  }

  private removeTask(taskId: string): void {
    const currentTasks: DownloadQueueTask[] = this.tasksSignal();
    const nextTasks: DownloadQueueTask[] = currentTasks.filter((task: DownloadQueueTask): boolean => task.id !== taskId);
    if (nextTasks.length === currentTasks.length) {
      return;
    }
    this.setTasks(nextTasks);
  }

  private updateTask(
    taskId: string,
    updater: (task: DownloadQueueTask) => DownloadQueueTask,
  ): void {
    const currentTasks: DownloadQueueTask[] = this.tasksSignal();
    let changed = false;

    const nextTasks: DownloadQueueTask[] = currentTasks.map((task: DownloadQueueTask): DownloadQueueTask => {
      if (task.id !== taskId) {
        return task;
      }
      changed = true;
      return updater(task);
    });

    if (!changed) {
      return;
    }

    this.setTasks(nextTasks);
  }

  private setTasks(nextTasks: DownloadQueueTask[]): void {
    this.tasksSignal.set(nextTasks);
    if (nextTasks.length <= 1) {
      this.expandedSignal.set(false);
    }
  }

  private finalizeQueueIfIdle(): void {
    if (this.tasksSignal().length > 0) {
      return;
    }

    this.expandedSignal.set(false);
  }

  private findTask(taskId: string): DownloadQueueTask | undefined {
    return this.tasksSignal().find((task: DownloadQueueTask): boolean => task.id === taskId);
  }

  private resolveDisplayFileName(fileName: string | null, sourceUrl: string): string {
    const trimmedName: string = typeof fileName === 'string' ? fileName.trim() : '';
    if (trimmedName) {
      return trimmedName;
    }

    const sourceWithoutQuery: string = sourceUrl.split('?')[0];
    const sourceWithoutHash: string = sourceWithoutQuery.split('#')[0];
    const segments: string[] = sourceWithoutHash.split('/');
    const encodedSegment: string = segments.length ? segments[segments.length - 1].trim() : '';
    if (!encodedSegment) {
      return this.translate.instant('downloadsToast.unknownFile');
    }

    try {
      const decoded = decodeURIComponent(encodedSegment).trim();
      return decoded || this.translate.instant('downloadsToast.unknownFile');
    } catch {
      return encodedSegment || this.translate.instant('downloadsToast.unknownFile');
    }
  }

  private createTaskId(): string {
    const randomPart: string = Math.random().toString(36).slice(2, 10);
    return `${Date.now()}-${randomPart}`;
  }
}
